import { IFunnelAnalysis } from '../../../utils/apollo/responseTypes';
import { assertCondition } from '../../../utils/types';
import { isNil } from 'lodash';
import { freeze } from '@reduxjs/toolkit';

type IOverallConversionReturn = { value: number; percent: number };

const ZERO: IOverallConversionReturn = freeze({
  percent: 0,
  value: 0,
});

export const getOverallConversion = (
  funnel: Pick<IFunnelAnalysis, 'data' | 'chartControl'>,
): IOverallConversionReturn => {
  assertCondition(funnel.data);
  assertCondition(funnel.data.dates.length === 1);

  if (funnel.chartControl.measureControls.length === 0) {
    return ZERO;
  }

  // -- First Step
  const firstStepKey = funnel.chartControl.measureControls[0].key;
  const firstStepColumns = funnel.data.columns.filter(
    (c) => c.measureKey === firstStepKey,
  );
  assertCondition(firstStepColumns.length <= 1);
  if (firstStepColumns.length === 0) {
    return ZERO;
  }
  const firstStepColumn = firstStepColumns[0];

  // -- Last step
  const lastStepIndex = funnel.chartControl.measureControls.length - 1;
  const lastStepKey = funnel.chartControl.measureControls[lastStepIndex].key;
  const lastStepColumns = funnel.data.columns.filter(
    (c) => c.measureKey === lastStepKey,
  );
  assertCondition(lastStepColumns.length <= 1);
  if (lastStepColumns.length === 0) {
    return ZERO;
  }

  const lastStepColumn = lastStepColumns[0];

  // edge case - there is only one step. Intentionally using === (identity equality),
  // it should be the same object (same column) if both start and end steps are the same
  if (firstStepColumn === lastStepColumn) {
    return {
      value: firstStepColumn.values[0] ?? 0,
      percent: 1,
    };
  }

  // -- Percent

  const percentColumn = lastStepColumn.percents.find(
    (p) => p.key === firstStepColumn.key,
  );

  if (isNil(percentColumn) || percentColumn.values.length === 0) {
    return ZERO;
  }

  return {
    percent: percentColumn.values[0] ?? 0,
    value: lastStepColumn.values[0] ?? 0,
  };
};
